import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FilterOption } from '../../../models/filterOption.interface';
import { FacetResults } from '../../../models/search-response.interface';

const ALL_RESULTS_LABEL = 'All Results';

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  standalone: false
})

export class FilterComponent implements OnInit, OnChanges {
  @Input() facetResponse: FacetResults;
  @Input() filterValues: string[] = [];
  @Input() selectedValues: string[] = [];
  @Output() selectionChange = new EventEmitter<string[]>();

  isOpen = false;
  filterOptions: FilterOption[] = [];
  allResult: FilterOption = {
    label: ALL_RESULTS_LABEL,
    count: 0,
    checked: false,
    disabled: false,
  };

  private initialCounts: { [label: string]: { count: number, disabled: boolean } } = {};

  constructor(private eRef: ElementRef) {}

  ngOnInit() {
    this.setInitialFacetOptions();
    this.setCheckedOptions();
    this.updateAllResultsOption();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['facetResponse'] || changes['filterValues']) {
      this.setInitialFacetOptions();
    }
    if (changes['selectedValues'] || changes['facetResponse'] || changes['filterValues']) {
      this.setCheckedOptions();
      this.updateAllResultsOption();
    }
  }

  setInitialFacetOptions() {
    this.initialCounts = {};
    if (this.facetResponse?.docCategory) {
      this.facetResponse.docCategory.forEach((val) => {
        this.initialCounts[val.value] = {
          count: val.count,
          disabled: val.count === 0,
        };
      });
    }
    this.filterOptions = this.filterValues.map((label) => {
      const data = this.initialCounts[label] || { count: 0, disabled: true };
      const disabled = data.disabled;
      return {
        label,
        count: data.count,
        checked: disabled,
        disabled
      };
    });
  }

  setCheckedOptions() {
    if (!this.filterOptions || this.filterOptions.length === 0) return;

    if (this.selectedValues === null || (this.selectedValues.length === 1 && this.selectedValues[0] === 'EMPTY')) {
      this.filterOptions.forEach(opt => {
        if (!opt.disabled) opt.checked = false;
      });
      return;
    }

    if (this.selectedValues.length === 1 && this.selectedValues[0] === 'ALL_SELECTED') {
      this.filterOptions.forEach(opt => {
        if (!opt.disabled) opt.checked = true;
      });
      return;
    }

    const currentSelection = this.selectedValues;
    this.filterOptions.forEach(opt => {
      if (!opt.disabled) opt.checked = currentSelection.includes(opt.label);
    });
  }

  updateAllResultsOption() {
    const totalCount = this.filterOptions.reduce((sum, opt) => sum + opt.count, 0);
    const enabledOptions = this.filterOptions.filter(opt => !opt.disabled);
    const checkedOptions = enabledOptions.filter(opt => opt.checked);

    this.allResult = {
      ...this.allResult,
      count: totalCount,
      checked: checkedOptions.length === enabledOptions.length && enabledOptions.length > 0,
      disabled: enabledOptions.length === 0,
    };

    this.setIndeterminateState(this.computeIndeterminate());
  }

  computeIndeterminate(): boolean {
    const enabledOptions = this.filterOptions.filter(opt => !opt.disabled);
    const checkedOptions = enabledOptions.filter(opt => opt.checked);
    return checkedOptions.length > 0 && checkedOptions.length < enabledOptions.length;
  }

  setIndeterminateState(indeterminate: boolean) {
    setTimeout(() => {
      const allResultCheckbox = document.getElementById('all-result-checkbox') as HTMLInputElement;
      if (allResultCheckbox) {
        allResultCheckbox.indeterminate = indeterminate;
      }
    });
  }

  toggleDropdown(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      setTimeout(() => {
        this.setIndeterminateState(this.computeIndeterminate());
      });
    }
  }

  handleOptionClick(option: FilterOption, event: Event) {
    event.stopPropagation();
    if (option.disabled) return;

    if (option.label === ALL_RESULTS_LABEL) {
      const newChecked = !this.allResult.checked;
      this.filterOptions.forEach(opt => {
        if (!opt.disabled) opt.checked = newChecked;
      });
    } else {
      option.checked = !option.checked;
    }

    this.updateAllResultsOption();
    this.emitSelection();
  }

  emitSelection() {
    const enabledOptions = this.filterOptions.filter(opt => !opt.disabled);
    const selected = this.filterOptions
      .filter(opt => opt.checked && !opt.disabled)
      .map(opt => opt.label);

    if (selected.length === enabledOptions.length && enabledOptions.length > 0) {
      this.selectionChange.emit(['ALL_SELECTED']);
      return;
    }

    if (selected.length === 0) {
      this.selectionChange.emit(['EMPTY']);
      return;
    }

    this.selectionChange.emit(selected);
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event) {
    if (this.isOpen && !this.eRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }
}